import mixpanel from 'mixpanel-browser';
import { APP_ENV } from '../utils/appEnv';
import { clarityEvent, clarityIdentify, claritySet } from '../utils/clarity';

const token = process.env.REACT_APP_MIXPANEL_KEY;

mixpanel.init(token, {
    debug: process.env.NODE_ENV !== 'production', // log in dev
    api_host: 'https://api.mixpanel.com', // change to api-eu.mixpanel.com if EU project
    // Don't keep the landing UTMs as super properties forever (and clear the ones a returning
    // browser already has): the signup puts them on signup_viewed and on the store's profile.
    stop_utm_persistence: true,
});

let env_check = !!token; // only disable if no token

// app_env: where the event comes from, so dev / local tests can be filtered out of the shared project.
if (env_check) mixpanel.register({ app_env: APP_ENV });

/**
 * The CMS shares the "Ecometri CMS" project with the API, which already tracks what happens after
 * the store exists (Sign up, Log in, Create product, Create category, Create order…). The frontend
 * only sends what the backend can't see: the signup funnel (v1 / v2 A/B), mostly before the account
 * exists. Every other `track` (old panel events, backend duplicates) is dropped here.
 */
const TRACKED_EVENTS = new Set([
    // Both variants
    'signup_viewed',
    '$experiment_started',
    'signup_started',
    'signup_validation_error',
    'signup_submit_failed',
    // Drop-off inside a screen (v1 form / v2 step): fields left filled, tab hidden / closed, came back
    'signup_field_completed',
    'signup_left',
    'signup_resumed',
    'signup_login_clicked',
    // v2 steps (each step's result is on its signup_step_completed, `skipped: true` when skipped)
    'signup_step_viewed',
    'signup_step_completed',
    'signup_step_back',
    'onboarding_niche_searched',
    'onboarding_niches_expanded',
    'onboarding_category_edited',
    'onboarding_preview_opened',
    'onboarding_photo_added',
    'onboarding_photo_failed',
    'onboarding_photo_limit_reached',
    // v2 after the account exists (not tracked by the API: it tracks each category / product)
    'onboarding_finalized',
    'onboarding_finalize_partial',
    'onboarding_finalize_failed',
    'onboarding_store_load_failed',
    'onboarding_completed',
    'onboarding_checklist_clicked',
    'store_shared',
    'store_viewed',
]);

let actions = {
    /**
     * Links this browser to a store (the store UUID is the distinct_id on both the API and the
     * CMS). If the browser is still identified as another store (expired session, no logout), it
     * starts a new anonymous identity first: under Original ID Merge an `$identify` from store A to
     * store B could merge both profiles for good. Only called at signup (see modules/auth signUp and
     * signup v2 WhatsAppStep), never on a regular login. Also identifies the Clarity session.
     */
    identify: (id) => {
        clarityIdentify(id);
        if (env_check) {
            const current = mixpanel.get_property('$user_id');
            if (current && current !== id) actions.reset();
            console.log('[Mixpanel] identify', id);
            mixpanel.identify(id);
        }
    },
    alias: (id) => {
        if (env_check) {
            console.log('[Mixpanel] alias', id);
            mixpanel.alias(id);
        }
    },
    /**
     * `options` goes to mixpanel.track (e.g. `{ transport: 'sendBeacon' }` when the page closes).
     * Tracked events are also Clarity events, tagged with the variant / step to filter recordings.
     */
    track: (name, props, options) => {
        if (!TRACKED_EVENTS.has(name)) {
            if (env_check) console.log('[Mixpanel] skipped (not a signup event)', name);
            return;
        }
        if (env_check) {
            console.log('[Mixpanel] track', name, props);
            mixpanel.track(name, props, options);
        }
        clarityEvent(name);
        claritySet('signup_variant', props && props.signup_variant);
        claritySet('step_name', props && props.step_name);
    },
    register: (props) => {
        claritySet('signup_variant', props && props.signup_variant);
        claritySet('store_type', props && props.store_type);
        if (env_check) {
            console.log('[Mixpanel] register', props);
            mixpanel.register(props);
        }
    },
    get_property: (name) => {
        if (env_check) {
            return mixpanel.get_property(name);
        }
        return undefined;
    },
    /** Clears the identity and super properties (new anonymous distinct_id). */
    reset: () => {
        if (env_check) {
            console.log('[Mixpanel] reset');
            mixpanel.reset();
            mixpanel.register({ app_env: APP_ENV });
        }
    },
    people: {
        set: (props) => {
            if (env_check) {
                console.log('[Mixpanel] people.set', props);
                mixpanel.people.set(props);
            }
        },
        set_once: (props) => {
            if (env_check) {
                console.log('[Mixpanel] people.set_once', props);
                mixpanel.people.set_once(props);
            }
        },
    },
};

export let Mixpanel = actions;
